import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";
import Navigation from "../components/Navigation";
import { getAccessToken } from "../utils/auth";

const UploadImagePage: React.FC = () => {
  const navigate = useNavigate();
  const [location, setLocation] = useState("");
  const [tagPath, setTagPath] = useState("");
  const [status, setStatus] = useState("Damaged");
  const [image, setImage] = useState<File | null>(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">("");
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!image) {
      setMessage("Please select an image file.");
      setMessageType("error");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("image", image);
    formData.append("location_taken", location);
    formData.append("tag_path", tagPath);
    formData.append("status", status);

    try {
      const res = await fetchWithAuthRetry(
        `https://api.adjusterphoto.com/images`,
        {
          method: "POST",
          body: formData,
          credentials: "include",
          headers: {
            Authorization: `Bearer ${getAccessToken() || ""}`,
          },
        },
        navigate,
      );

      if (!res.ok) throw new Error("Upload failed");

      setMessage("Image uploaded successfully!");
      setMessageType("success");
      setLocation("");
      setTagPath("");
      setStatus("Damaged");
      setImage(null);

      // Reset file input
      const fileInput = document.getElementById("imageInput") as HTMLInputElement;
      if (fileInput) fileInput.value = "";
    } catch (err) {
      console.error(err);
      setMessage("Failed to upload image.");
      setMessageType("error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0b0f]">
      <Navigation />
      <div className="py-10 px-4">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 opacity-0 animate-slide-up stagger-1">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 mb-4 shadow-lg shadow-amber-500/20">
              <svg className="w-7 h-7 text-[#0a0b0f]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-zinc-100">Upload Image</h1>
            <p className="text-zinc-500 mt-1 text-sm">Add a new photo to your collection</p>
          </div>

          {/* Form Card */}
          <div className="card-dark rounded-2xl p-6 shadow-xl opacity-0 animate-slide-up stagger-2">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Location Taken
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="input-dark w-full px-4 py-3 rounded-xl text-zinc-100"
                  placeholder="e.g., 123 Main St, Chicago"
                  required
                />
              </div>

              {/* Tag Path */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Tag Path
                </label>
                <input
                  type="text"
                  value={tagPath}
                  onChange={(e) => setTagPath(e.target.value)}
                  className="input-dark w-full px-4 py-3 rounded-xl text-zinc-100"
                  placeholder="e.g., Roof/Shingles"
                  required
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="select-dark w-full px-4 py-3 rounded-xl"
                  required
                >
                  <option value="Damaged">Damaged</option>
                  <option value="Fixed">Fixed</option>
                  <option value="Overview">Overview</option>
                </select>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Image
                </label>
                <input
                  id="imageInput"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      setImage(e.target.files[0]);
                    }
                  }}
                  className="file-input-dark w-full"
                  required
                />
                {image && (
                  <p className="mt-2 text-sm text-zinc-500">
                    Selected: {image.name}
                  </p>
                )}
              </div>

              {/* Message */}
              {message && (
                <div className={messageType === "success" ? "alert-success" : "alert-error"}>
                  {message}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={uploading}
                className="btn-primary w-full py-3.5 rounded-xl text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {uploading ? (
                  <span className="inline-flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Uploading...
                  </span>
                ) : (
                  "Upload Image"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadImagePage;
